import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Download, Plus, Search, Sparkles, Star } from 'lucide-react';
import { COCKTAIL_STATUSES, type CocktailStatus } from '@muddle/core';
import { api } from '../api';
import { useAsync } from '../hooks/useAsync';
import { ErrorMessage, PageHeader, Skeleton, StatusPill, useToast } from '../components';
import { saveFromApi, today } from '../fileSave';
import { useTitle } from '../shortcuts';

export function LibraryPage() {
  const navigate = useNavigate();
  const toast = useToast();
  useTitle('Library');
  const searchRef = useRef<HTMLInputElement>(null);
  const location = useLocation();
  // Ctrl+F from another page lands here and focuses the search box.
  useEffect(() => {
    if ((location.state as { focusSearch?: boolean } | null)?.focusSearch)
      searchRef.current?.focus();
  }, [location.state]);
  const [selecting, setSelecting] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [exporting, setExporting] = useState(false);
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [status, setStatus] = useState<CocktailStatus | ''>('');
  const [tag, setTag] = useState('');
  const [spirit, setSpirit] = useState('');
  const [archived, setArchived] = useState(false);
  const [minRating, setMinRating] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q), 200);
    return () => clearTimeout(t);
  }, [q]);

  const [list, reload] = useAsync(
    () =>
      api.listCocktails({
        q: debounced,
        status: status || undefined,
        tag: tag || undefined,
        spirit: spirit || undefined,
        archived,
        minRating: minRating || undefined,
      }),
    [debounced, status, tag, spirit, archived, minRating],
  );
  const [tags] = useAsync(api.listTags);
  const [ingredients] = useAsync(() => api.listIngredients());
  const spirits =
    ingredients.status === 'ready' ? ingredients.data.filter((i) => i.category === 'spirit') : [];
  const toggle = (id: string) =>
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const stopSelecting = () => {
    setSelecting(false);
    setSelected(new Set());
  };
  const exportSelected = async () => {
    setExporting(true);
    try {
      const r = await saveFromApi(
        `/export/xlsx?ids=${[...selected].join(',')}`,
        `muddle-cocktails-${today()}.xlsx`,
      );
      if (r.saved) toast(`Exported ${selected.size} drink${selected.size === 1 ? '' : 's'}`);
      stopSelecting();
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Export failed.', { error: true });
    } finally {
      setExporting(false);
    }
  };
  // Secondary filters stay tucked away until you want them (or one is active).
  const activeMore = [tag, spirit, minRating ? 'r' : '', archived ? 'a' : ''].filter(
    Boolean,
  ).length;
  const [moreOpen, setMoreOpen] = useState(false);
  const filtering = Boolean(debounced || status || tag || spirit || minRating);

  return (
    <div className="page">
      <PageHeader
        kicker="Library"
        title={archived ? 'Archived drinks' : 'Your drinks'}
        sub="Every recipe, with its full version history."
        actions={
          <>
            <button
              className="btn btn-ghost"
              type="button"
              aria-pressed={selecting}
              onClick={() => (selecting ? stopSelecting() : setSelecting(true))}
            >
              {selecting ? 'Done' : 'Select'}
            </button>
            <button className="btn btn-ghost" type="button" onClick={() => navigate('/drinks/ask')}>
              <Sparkles size={16} aria-hidden /> Describe a drink
            </button>
            <button
              className="btn btn-primary"
              type="button"
              onClick={() => navigate('/drinks/new')}
            >
              <Plus size={16} aria-hidden /> New drink
            </button>
          </>
        }
      />

      <div className="mu-filters">
        <div className="mu-search">
          <Search size={16} aria-hidden />
          <label className="sr-only" htmlFor="q">
            Search drinks
          </label>
          <input
            id="q"
            ref={searchRef}
            type="search"
            placeholder="Search by name or tag"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <div className="chip-row" role="group" aria-label="Status">
          <button
            type="button"
            className="chip"
            aria-pressed={status === ''}
            onClick={() => setStatus('')}
          >
            All
          </button>
          {COCKTAIL_STATUSES.map((s) => (
            <button
              key={s}
              type="button"
              className="chip"
              aria-pressed={status === s}
              onClick={() => setStatus(s)}
            >
              {s}
            </button>
          ))}
        </div>
        <details
          className="mu-more"
          open={moreOpen}
          onToggle={(e) => setMoreOpen(e.currentTarget.open)}
        >
          <summary>
            More filters
            {activeMore > 0 && <span className="pill">{activeMore} active</span>}
          </summary>
          <div className="row row-3">
            <div>
              <label htmlFor="tag">Tag</label>
              <select id="tag" value={tag} onChange={(e) => setTag(e.target.value)}>
                <option value="">Any tag</option>
                {tags.status === 'ready' && tags.data.map((t) => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="spirit">Spirit</label>
              <select id="spirit" value={spirit} onChange={(e) => setSpirit(e.target.value)}>
                <option value="">Any spirit</option>
                {spirits.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="rating">Rating</label>
              <select
                id="rating"
                value={minRating}
                onChange={(e) => setMinRating(Number(e.target.value))}
              >
                <option value={0}>Any rating</option>
                {[5, 4, 3, 2].map((n) => (
                  <option key={n} value={n}>
                    {n}+ (latest tasting)
                  </option>
                ))}
              </select>
            </div>
          </div>
          <label className="check-row">
            <input
              type="checkbox"
              checked={archived}
              onChange={(e) => setArchived(e.target.checked)}
            />{' '}
            Show archived
          </label>
        </details>
      </div>

      {list.status === 'loading' && <Skeleton />}
      {list.status === 'error' && (
        <ErrorMessage message={`Couldn't load your drinks. ${list.message}`} onRetry={reload} />
      )}
      {list.status === 'ready' && list.data.length === 0 && (
        <div className="card empty">
          <div className="empty-title">
            {filtering ? 'No drinks match' : archived ? 'Nothing archived' : 'No drinks yet'}
          </div>
          {filtering
            ? 'Try a different search or clear the filters.'
            : archived
              ? 'Drinks you archive show up here and can be restored.'
              : 'Create your first recipe and track every tweak.'}
          {!filtering && !archived && (
            <div>
              <button
                className="btn btn-primary"
                type="button"
                onClick={() => navigate('/drinks/new')}
              >
                New drink
              </button>
            </div>
          )}
        </div>
      )}
      {list.status === 'ready' && list.data.length > 0 && (
        <section className="card mu-list" aria-label="Drinks">
          {list.data.map((c) => {
            const content = (
              <>
                <div className="grow">
                  <div className="row-title truncate">{c.name}</div>
                  <div className="row-meta">
                    {[
                      c.currentVersionNumber ? `v${c.currentVersionNumber}` : null,
                      c.technique,
                      c.glass || null,
                      c.spirits.join(', ') || null,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </div>
                  {c.tags.length > 0 && (
                    <div className="row-meta">{c.tags.map((t) => `#${t}`).join(' ')}</div>
                  )}
                </div>
                {c.latestRating !== null && (
                  <span
                    className="mu-rating num"
                    aria-label={`Latest rating ${c.latestRating} out of 5`}
                  >
                    <Star size={14} aria-hidden /> {c.latestRating}
                  </span>
                )}
                <StatusPill status={c.status} />
              </>
            );
            return selecting ? (
              <label key={c.id} className="list-row mu-drink-row mu-select-row">
                <input
                  type="checkbox"
                  checked={selected.has(c.id)}
                  onChange={() => toggle(c.id)}
                  aria-label={`Select ${c.name}`}
                />
                {content}
              </label>
            ) : (
              <Link key={c.id} to={`/drinks/${c.id}`} className="list-row mu-drink-row">
                {content}
              </Link>
            );
          })}
        </section>
      )}
      {selecting && (
        <div className="actions mu-sticky-actions" role="region" aria-label="Export selection">
          <button
            className="btn btn-primary"
            type="button"
            disabled={selected.size === 0 || exporting}
            aria-busy={exporting}
            onClick={() => void exportSelected()}
          >
            <Download size={16} aria-hidden />{' '}
            {exporting ? 'Exporting…' : `Export ${selected.size} to Excel`}
          </button>
          {list.status === 'ready' && (
            <button
              className="btn btn-ghost"
              type="button"
              onClick={() => setSelected(new Set(list.data.map((c) => c.id)))}
            >
              Select all shown
            </button>
          )}
        </div>
      )}
    </div>
  );
}
